import { ReactElement, ReactNode, useMemo } from 'react';

import { FormattedMessage } from 'react-intl';

import { useNumberPages } from 'hooks/useNumberPages';
import { EmptyState, Pagination, ResponsiveTable } from 'views/components';
import { DialogStack } from 'views/components/UI/Modal';
import {
  IResponsiveTableColumn,
  IResponsiveTableRow,
  IResponsiveTableRowSelection,
} from 'views/components/UI/ResponsiveTable';

const TEMPLATE_PAGE_SIZE = 6;

interface ITemplate<T extends IResponsiveTableRow> {
  columns: IResponsiveTableColumn<T>[];
  rowSelection: IResponsiveTableRowSelection;
  arrayItems: T[];
  renderCell?: (column: IResponsiveTableColumn<T>, row: T) => ReactNode;
}

const Template = <T extends IResponsiveTableRow>(props: ITemplate<T>): ReactElement => {
  const { rowSelection, arrayItems, columns, renderCell } = props;

  const {
    indexOfFirstPost,
    indexOfLastPost,
    setCurrentPage,
    currentPage,
    MAX_COUNT_ELEMENTS,
  } = useNumberPages(TEMPLATE_PAGE_SIZE);

  const shouldShowPagination = arrayItems.length > MAX_COUNT_ELEMENTS;

  const currentItems = useMemo(
    () => arrayItems.slice(indexOfFirstPost, indexOfLastPost),
    [arrayItems, currentPage],
  );

  return (
    <>
      {arrayItems.length > 0 ? (
        <DialogStack>
          <ResponsiveTable
            columns={columns}
            data={currentItems}
            renderCell={renderCell}
            rowSelection={rowSelection}
          />
          {shouldShowPagination && (
            <Pagination
              itemsPerPage={MAX_COUNT_ELEMENTS}
              currentPage={currentPage}
              totalItems={Number(arrayItems.length)}
              handlePageChange={page => setCurrentPage(page)}
            />
          )}
        </DialogStack>
      ) : (
        <EmptyState
          title={<FormattedMessage id="expertise-management.modal.no-data.title" />}
          description={
            <FormattedMessage id="expertise-management.modal.no-data.description" />
          }
        />
      )}
    </>
  );
};

export { Template };
